import type { AppointmentTypeName } from "@src/constants";
import { APPOINTMENT_TYPE_NAME } from "@src/constants/scheduling-constants";
import { callApi, extractApiErrorMessage } from "@src/utils/api-client";
import { getFailureTypeForHttpStatus } from "@src/utils/failure-taxonomy";
import type { ScheduleAppointmentRequest } from "@src/utils/schedule-appointment-utils";
import { normalizeString } from "@src/utils/string-utils";
import ClientLogsService from "./client-logs.service";
import { buildScheduleAppointmentConfig } from "./configs";
import PollingStatusService, {
  type PollingStatusResponse,
  type PollingStatusStep,
  type PollingStepName,
  type PollingStepStatus,
} from "./polling-status.service";
import { runManagedRequest } from "./request-manager";

/**
 * Canonical schedule-appointment request model reused from shared transform utils.
 */
export type ScheduleAppointmentPayload = ScheduleAppointmentRequest;

/**
 * Response interface for scheduling an appointment
 */
export interface ScheduleAppointmentResponse {
  success: boolean;
  message: string;
  pollingId?: string;
  statusCode?: number;
  error?: string;
}

interface BackendScheduleAppointmentResponse {
  message?: string;
  polling_id?: string;
  error?: string;
  code?: string;
  restriction_type?: string;
  reason?: string;
}

type ScheduleAppointmentRequestPayload = Partial<ScheduleAppointmentPayload> & {
  appointment_type_name: AppointmentTypeName;
  user_uuid: string;
  patient_dob: string;
  appointment_datetime: string;
};

export type { ScheduleAppointmentRequestPayload };

export type SchedulePollingWorkflowResult =
  | {
      kind: "success";
      message: string;
      statusCode?: number;
      usedPolling: boolean;
    }
  | {
      kind: "schedule_failure";
      message: string;
      statusCode?: number;
      error?: string;
    }
  | {
      kind: "completed_with_failure";
      message: string;
      statusCode?: number;
      pollingId: string;
    }
  | {
      // Polling stopped before any terminal state (success/failure) was reached.
      kind: "no_terminal";
      message: string;
      statusCode?: number;
      pollingId: string;
    };

export type ScheduleAppointmentWithPollingDomainParams = {
  schedulePayload: ScheduleAppointmentRequestPayload;
  userUuid: string;
  patientDOB: string;
  pollingIntervalMs: number;
  signal?: AbortSignal;
  onAsyncAccepted?: (context: { pollingId: string; statusCode?: number }) => void;
  onProgressUpdate?: (update: SchedulePollingProgressUpdate) => void;
};

const CRITICAL_PROGRESS_STEP_NAMES = [
  "Create Patient",
  "Create Insurance",
  "Create Appointment",
] as const satisfies readonly PollingStepName[];

export type SchedulePollingCriticalStepName = (typeof CRITICAL_PROGRESS_STEP_NAMES)[number];

export type SchedulePollingProgressSteps = Record<
  SchedulePollingCriticalStepName,
  PollingStepStatus
>;

export type SchedulePollingProgressUpdate = Pick<
  PollingStatusResponse,
  "isTerminal" | "isSuccess" | "message"
> & {
  pollingId: string;
  steps: SchedulePollingProgressSteps;
};

const DEFAULT_POLLING_INTERVAL_MS = 2000;
const DEFAULT_CRITICAL_PROGRESS_STEPS = Object.fromEntries(
  CRITICAL_PROGRESS_STEP_NAMES.map((stepName) => {
    return [stepName, "pending"] as const;
  }),
) as SchedulePollingProgressSteps;

const toCriticalProgressSteps = (steps: PollingStatusStep[]): SchedulePollingProgressSteps => {
  const mergedSteps: SchedulePollingProgressSteps = {
    ...DEFAULT_CRITICAL_PROGRESS_STEPS,
  };

  for (const stepName of CRITICAL_PROGRESS_STEP_NAMES) {
    const matchingStep = steps.find((step) => step.stepName === stepName);
    if (matchingStep) {
      mergedSteps[stepName] = matchingStep.status;
    }
  }

  return mergedSteps;
};

const extractScheduleAppointmentMessage = (payload: unknown): string | undefined => {
  if (typeof payload !== "object" || payload === null) {
    return undefined;
  }
  return normalizeString((payload as { message?: unknown }).message);
};

const MISSING_POLLING_ID_ERROR = "Missing polling_id for accepted request";

const waitForPollingInterval = (intervalMs: number, signal?: AbortSignal): Promise<void> => {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }

    const timeoutId = globalThis.setTimeout(() => {
      if (signal) {
        signal.removeEventListener("abort", onAbort);
      }
      resolve();
    }, intervalMs);

    const onAbort = () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
      resolve();
    };

    signal?.addEventListener("abort", onAbort, { once: true });
  });
};

const buildScheduleAppointmentRequestKey = (
  prefix: string,
  payload: ScheduleAppointmentRequestPayload,
): string => {
  return `${prefix}:${payload.user_uuid ?? "unknown"}:${payload.provider_id ?? "unknown"}:${
    Array.isArray(payload.appointment_id)
      ? payload.appointment_id.join(",")
      : (payload.appointment_id ?? "unknown")
  }:${payload.appointment_datetime ?? "unknown"}`;
};

const validateScheduleAppointmentRequest = (
  payload: ScheduleAppointmentRequestPayload,
): ScheduleAppointmentResponse | null => {
  if (payload.appointment_type_id !== undefined && payload.appointment_type_id !== null) {
    const normalizedId = Number(payload.appointment_type_id);
    if (!Number.isInteger(normalizedId) || normalizedId <= 0) {
      return {
        success: false,
        error: "Invalid appointment_type_id",
        message: "Invalid appointment_type_id",
      };
    }
  }

  if (!Object.values(APPOINTMENT_TYPE_NAME).includes(payload.appointment_type_name)) {
    return {
      success: false,
      error: "Invalid appointment_type_name",
      message: "Invalid appointment_type_name",
    };
  }

  return null;
};

type ScheduleAppointmentRequestContext = {
  payload: ScheduleAppointmentRequestPayload;
  responsePayload: unknown;
  response: Response;
  backendPayload: BackendScheduleAppointmentResponse;
  responseMessage: string;
};

const runScheduleAppointmentRequest = async (
  payload: ScheduleAppointmentRequestPayload,
  options: {
    keyPrefix: string;
    logContext: string;
    requestHandler: (
      context: ScheduleAppointmentRequestContext,
    ) => Promise<ScheduleAppointmentResponse> | ScheduleAppointmentResponse;
  },
): Promise<ScheduleAppointmentResponse> => {
  const validationError = validateScheduleAppointmentRequest(payload);
  if (validationError) {
    return validationError;
  }

  const key = buildScheduleAppointmentRequestKey(options.keyPrefix, payload);

  try {
    return await runManagedRequest({
      key,
      policy: "single-flight",
      request: async (signal) => {
        const { data: responsePayload, response } = await callApi(
          buildScheduleAppointmentConfig(payload, signal),
        );

        if (responsePayload === null) {
          return {
            success: false,
            error: "Invalid JSON response",
            message: "Invalid JSON response",
          };
        }

        const backendPayload = responsePayload as BackendScheduleAppointmentResponse;
        const responseMessage =
          extractScheduleAppointmentMessage(responsePayload) || "Failed to schedule appointment";

        return options.requestHandler({
          payload,
          responsePayload,
          response,
          backendPayload,
          responseMessage,
        });
      },
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";

    ClientLogsService.logError(
      payload.user_uuid || "unknown",
      options.logContext,
      "Error scheduling appointment",
      error instanceof Error ? error : new Error("Unknown error"),
      {
        errorType: "DependencyFailure",
        dependencyName: "ScheduleAppointmentAPI",
        functionName: "runScheduleAppointmentRequest",
      },
    );

    return {
      success: false,
      error: errorMessage,
      message: errorMessage,
    };
  }
};

// biome-ignore lint/complexity/noStaticOnlyClass: pre-existing pattern, refactor out of scope
export class ScheduleAppointmentService {
  static async scheduleAppointmentWithPollingDomain({
    schedulePayload,
    userUuid,
    patientDOB,
    pollingIntervalMs,
    signal,
    onAsyncAccepted,
    onProgressUpdate,
  }: ScheduleAppointmentWithPollingDomainParams): Promise<SchedulePollingWorkflowResult> {
    const response =
      await ScheduleAppointmentService.scheduleAppointmentForPolling(schedulePayload);

    if (!response.success) {
      return {
        kind: "schedule_failure",
        message: response.error || response.message,
        error: response.error,
        statusCode: response.statusCode,
      };
    }

    if (!response.pollingId) {
      return {
        kind: "success",
        message: response.message,
        statusCode: response.statusCode,
        usedPolling: false,
      };
    }

    const resolvedPollingIntervalMs =
      Number.isFinite(pollingIntervalMs) && pollingIntervalMs > 0
        ? Math.floor(pollingIntervalMs)
        : DEFAULT_POLLING_INTERVAL_MS;

    onAsyncAccepted?.({
      pollingId: response.pollingId,
      statusCode: response.statusCode,
    });

    try {
      while (!signal?.aborted) {
        const pollingStatusResponse = await PollingStatusService.getPollingStatus(
          response.pollingId,
          userUuid,
        );

        onProgressUpdate?.({
          pollingId: response.pollingId,
          steps: toCriticalProgressSteps(pollingStatusResponse.steps),
          isTerminal: pollingStatusResponse.isTerminal,
          isSuccess: pollingStatusResponse.isSuccess,
          message: pollingStatusResponse.message,
        });

        if (pollingStatusResponse.isTerminal) {
          if (!pollingStatusResponse.success || !pollingStatusResponse.isSuccess) {
            return {
              kind: "completed_with_failure",
              message: pollingStatusResponse.message,
              statusCode: response.statusCode,
              pollingId: response.pollingId,
            };
          }

          return {
            kind: "success",
            message: pollingStatusResponse.message,
            statusCode: response.statusCode,
            usedPolling: true,
          };
        }

        await waitForPollingInterval(resolvedPollingIntervalMs, signal);
      }
    } catch (pollingError) {
      const pollingErrorMessage =
        pollingError instanceof Error ? pollingError.message : "Unknown polling error";

      const noTerminalErrorMessage = `Polling execution failed: ${pollingErrorMessage}`;
      return {
        kind: "no_terminal",
        message: noTerminalErrorMessage,
        statusCode: response.statusCode,
        pollingId: response.pollingId,
      };
    }

    const noTerminalMessage = "Polling ended before reaching a terminal status";
    return {
      kind: "no_terminal",
      message: noTerminalMessage,
      statusCode: response.statusCode,
      pollingId: response.pollingId,
    };
  }

  static async scheduleAppointmentForPolling(
    payload: ScheduleAppointmentRequestPayload,
  ): Promise<ScheduleAppointmentResponse> {
    return runScheduleAppointmentRequest(payload, {
      keyPrefix: "schedule-polling",
      logContext: "schedule-appointment.service.scheduleAppointmentForPolling",
      requestHandler: async ({
        payload: requestPayload,
        responsePayload,
        response,
        backendPayload,
        responseMessage,
      }) => {
        if (!response.ok) {
          const responseError = extractApiErrorMessage(responsePayload, response);

          return {
            success: false,
            error: responseError,
            message: responseMessage,
            statusCode: response.status,
          };
        }

        const backendPollingId = normalizeString(backendPayload.polling_id);
        if (response.status === 202) {
          if (!backendPollingId) {
            return {
              success: false,
              message: MISSING_POLLING_ID_ERROR,
              error: MISSING_POLLING_ID_ERROR,
              statusCode: response.status,
            };
          }

          return {
            success: true,
            message: responseMessage,
            pollingId: backendPollingId,
            statusCode: response.status,
          };
        }

        return {
          success: true,
          message: responseMessage,
          statusCode: response.status,
        };
      },
    });
  }

  /**
   * Schedule an appointment
   * @param payload - Appointment scheduling data
   * @returns Response from the API
   */
  static async scheduleAppointment(
    payload: ScheduleAppointmentRequestPayload,
  ): Promise<ScheduleAppointmentResponse> {
    return runScheduleAppointmentRequest(payload, {
      keyPrefix: "schedule",
      logContext: "schedule-appointment.service.scheduleAppointment",
      requestHandler: async ({
        payload: requestPayload,
        response,
        responsePayload,
        responseMessage,
      }) => {
        if (!response.ok) {
          const responseError = extractApiErrorMessage(responsePayload, response);

          ClientLogsService.logError(
            requestPayload.user_uuid || "unknown",
            "schedule-appointment.service.scheduleAppointment",
            "Failed to schedule appointment",
            new Error(responseError),
            {
              errorType: getFailureTypeForHttpStatus(response.status),
              dependencyName: "ScheduleAppointmentAPI",
              functionName: "scheduleAppointment",
            },
          );

          return {
            success: false,
            error: responseError,
            message: responseMessage,
          };
        }

        return {
          success: true,
          message: responseMessage,
        };
      },
    });
  }
}

export default ScheduleAppointmentService;
